import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GroupIcon from "@mui/icons-material/Group";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Button from "components/button";
import PackageDealReserveModal from "components/packageDealReserveModal";
import usePackageDeal from "hooks/usePackageDeal";
import { useTranslation } from "i18n";
import moment from "moment";
import { CurrencyContext } from "pages/useContext";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getPackageDealPrice,
  getCheapestDealPrice,
  hasRoomTypePricing,
  getRoomCombinations,
  getCombinationBreakdown,
} from "utils/packageDealPrice";
import "./details.css";

function PackageDealDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currency } = useContext(CurrencyContext);
  const { deal, loading, error } = usePackageDeal(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const useRoomPricing = deal && hasRoomTypePricing(deal.price);
  const maxPeople = Math.min(deal?.maxParticipants || 20, 20);
  const [numPeople, setNumPeople] = useState(1);
  const [selectedCombination, setSelectedCombination] = useState(null);
  const [reserveModalOpen, setReserveModalOpen] = useState(false);

  const combinations = useMemo(
    () => (useRoomPricing && deal?.price ? getRoomCombinations(numPeople, deal.price, currency) : []),
    [useRoomPricing, numPeople, deal?.price, currency]
  );

  const combinationsWithPrice = useMemo(() => {
    if (!useRoomPricing || !deal?.price) return [];
    return combinations
      .map((combo) => {
        const { total, breakdown } = getCombinationBreakdown(deal.price, combo.rooms, currency);
        return {
          ...combo,
          price: total,
          breakdown,
          priceKey: `${combo.rooms.map((r) => `${r.type}${r.count}`).join("-")}`,
        };
      })
      .filter((c) => c.price > 0)
      .sort((a, b) => a.price - b.price);
  }, [useRoomPricing, deal?.price, combinations, currency]);

  const cheapestCombo = combinationsWithPrice[0];
  const displayCombo = selectedCombination || cheapestCombo;
  const priceInfo = useMemo(() => {
    if (!deal) return null;
    if (useRoomPricing && displayCombo) {
      const base = getCheapestDealPrice(deal.price, currency);
      const amount = displayCombo.price;
      const formatted =
        amount % 1 === 0
          ? String(amount)
          : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return { ...base, amount, formatted, selectedCombo: displayCombo };
    }
    if (useRoomPricing) return getCheapestDealPrice(deal.price, currency);
    return getPackageDealPrice(deal.price, currency);
  }, [deal, currency, useRoomPricing, displayCombo]);

  useEffect(() => {
    if (numPeople < 1) setNumPeople(1);
    if (numPeople > maxPeople) setNumPeople(maxPeople);
  }, [numPeople, maxPeople]);

  useEffect(() => {
    if (useRoomPricing && combinationsWithPrice.length > 0) {
      setSelectedCombination((prev) => {
        const cheapest = combinationsWithPrice[0];
        if (!prev) return cheapest;
        // Keep same selection but use combo from current list (has updated prices for current currency)
        const match = combinationsWithPrice.find((c) => c.priceKey === prev.priceKey);
        return match || cheapest;
      });
    }
  }, [useRoomPricing, numPeople, combinationsWithPrice]);

  const handleReserveClick = () => {
    if (!deal) return;
    setReserveModalOpen(true);
  };

  if (loading) {
    return (
      <div className="pdp">
        <div className="pdp__loader">
          <CircularProgress sx={{ color: "var(--primary-color)" }} size={48} />
          <p className="pdp__loader-text">{t("packageDeals.tripLoading")}</p>
        </div>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="pdp">
        <div className="pdp__error">
          <span className="pdp__error-icon">✕</span>
          <h2>{t("packageDeals.detailsLoadErrorTitle")}</h2>
          <p>{t("packageDeals.detailsLoadErrorDescription")}</p>
          <Button
            onClick={() => navigate("/package-deals")}
            className="primary-color"
          >
            {t("packageDeals.backToTours")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdp">
      <button
        type="button"
        onClick={() => navigate("/package-deals")}
        className="pdp__back"
        aria-label={t("packageDeals.backToTours")}
      >
        <ArrowBackIcon />
        <span>{t("packageDeals.title")}</span>
      </button>

      <article className="pdp__article">
        <header className="pdp__hero">
          <div className="pdp__hero-bg">
            {deal.imageUrls?.[0] ? (
              <img src={deal.imageUrls[0]} alt="" />
            ) : (
              <div className="pdp__hero-placeholder" />
            )}
            <div className="pdp__hero-shade" />
          </div>
          <div className="pdp__hero-content">
            <h1 className="pdp__title">{deal.title}</h1>
            <div className="pdp__meta">
              <span className="pdp__meta-item">
                <ScheduleIcon />
                {deal.duration}{" "}
                {deal.duration === 1
                  ? t("packageDeals.night")
                  : t("packageDeals.nights")}
              </span>
              {deal.validFrom && (
                <span className="pdp__meta-item">
                  <CalendarMonthIcon />
                  {moment(deal.validFrom).format("MMM D")} –{" "}
                  {moment(deal.validUntil).format("MMM D, YYYY")}
                </span>
              )}
            </div>
          </div>
        </header>

        <div className="pdp__main">
          {deal.description && (
            <section className="pdp__block pdp__block--animate">
              <h2 className="pdp__block-title">{t("packageDeals.aboutPackage")}</h2>
              <p className="pdp__overview">{deal.description}</p>
            </section>
          )}

          {useRoomPricing && (
            <section className="pdp__block pdp__block--animate">
              <h2 className="pdp__block-title">{t("packageDeals.peopleQuestion")}</h2>
              <p className="pdp__block-desc pdp__people-dropdown-hint">
                {t("packageDeals.groupSizeHint")}
              </p>
              <div className="pdp__people-dropdown">
                <FormControl
                  fullWidth
                  variant="outlined"
                  size="medium"
                  sx={{ minWidth: 200, maxWidth: 280, mb: 3 }}
                  className="pdp__people-select"
                >
                  <Select
                    value={numPeople}
                    onChange={(e) => setNumPeople(Number(e.target.value))}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      "& .MuiSelect-select": { py: 1.75, pr: 3.5 },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0,0,0,0.4)",
                        borderWidth: "2px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--primary-color, #f15e22)",
                        borderWidth: "2px",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--primary-color, #f15e22)",
                        borderWidth: "2px",
                      },
                    }}
                    MenuProps={{
                      PaperProps: { sx: { borderRadius: 2, mt: 1.5 } },
                    }}
                  >
                    {Array.from({ length: maxPeople }, (_, i) => i + 1).map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}{" "}
                        {n === 1
                          ? t("packageDeals.person")
                          : t("packageDeals.people")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              {combinationsWithPrice.length > 0 && (
                <>
                  <h3 className="pdp__block-subtitle">{t("packageDeals.roomOptions")}</h3>
                  <p className="pdp__block-desc">
                    {t("packageDeals.roomOptionsDescription", {
                      count: numPeople,
                      people:
                        numPeople === 1
                          ? t("packageDeals.person")
                          : t("packageDeals.people"),
                    })}
                  </p>
                  <div className="pdp__combinations">
                    {combinationsWithPrice.map((combo) => (
                      <button
                        key={combo.priceKey}
                        type="button"
                        className={`pdp__combo-card ${selectedCombination?.priceKey === combo.priceKey ? "pdp__combo-card--selected" : ""}`}
                        onClick={() => setSelectedCombination(combo)}
                      >
                        <span className="pdp__combo-label">{combo.label}</span>
                        {combo.breakdown?.length > 0 && (
                          <div className="pdp__combo-breakdown">
                            {combo.breakdown.map((line, i) => {
                              const people = line.occupants * line.count;
                              return (
                                <span key={i} className="pdp__combo-breakdown-line">
                                  {line.label}: {priceInfo?.sign}
                                  {(line.pricePerPerson % 1 === 0 ? line.pricePerPerson : line.pricePerPerson.toFixed(2))}/{t("packageDeals.person")} × {people} {people === 1 ? t("packageDeals.person") : t("packageDeals.people")} = {priceInfo?.sign}
                                  {line.lineTotal % 1 === 0 ? line.lineTotal : line.lineTotal.toFixed(2)}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <span className="pdp__combo-price">
                          {t("packageDeals.total")}: {priceInfo?.sign}
                          {combo.price % 1 === 0
                            ? combo.price.toLocaleString()
                            : combo.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </section>
          )}

          {deal.itinerary?.length > 0 && (
            <section className="pdp__block pdp__block--animate">
              <h2 className="pdp__block-title">{t("packageDeals.travelProgramme")}</h2>
              <div className="pdp__timeline">
                <div className="pdp__timeline-line" aria-hidden="true" />
                {deal.itinerary.map((day, index) => (
                  <div
                    key={day.day ?? index}
                    className="pdp__timeline-item"
                    style={{ animationDelay: `${index * 80}ms` }}
                  >
                    <div className="pdp__timeline-dot">
                      <span>{t("packageDeals.day", { day: day.day })}</span>
                    </div>
                    <div className="pdp__timeline-card">
                      {day.title && (
                        <h3 className="pdp__timeline-title">{day.title}</h3>
                      )}
                      {day.description && (
                        <p className="pdp__timeline-desc">{day.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="pdp__lists">
            {deal.included?.length > 0 && (
              <section className="pdp__list-block pdp__block--animate">
                <h2 className="pdp__list-title">
                  <CheckCircleOutlineIcon className="pdp__list-icon pdp__list-icon--included" />
                  {t("packageDeals.included")}
                </h2>
                <ul className="pdp__bullet-list pdp__bullet-list--included">
                  {deal.included.map((item, i) => (
                    <li key={i}>{String(item || "").trim()}</li>
                  ))}
                </ul>
              </section>
            )}
            {deal.excluded?.length > 0 && (
              <section className="pdp__list-block pdp__block--animate">
                <h2 className="pdp__list-title">
                  <CancelOutlinedIcon className="pdp__list-icon pdp__list-icon--excluded" />
                  {t("packageDeals.notIncluded")}
                </h2>
                <ul className="pdp__bullet-list pdp__bullet-list--excluded">
                  {deal.excluded.map((item, i) => (
                    <li key={i}>{String(item || "").trim()}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>

        <aside className="pdp__cta" role="complementary">
          <div className="pdp__cta-inner">
            <div className="pdp__cta-price">
              <span className="pdp__cta-label">{t("packageDeals.from")}</span>
              <span className="pdp__cta-value">
                {priceInfo?.sign}
                {priceInfo?.formatted}
              </span>
              {deal.maxParticipants && (
                <span className="pdp__cta-note">
                  {t("packageDeals.maxParticipants", {
                    count: deal.maxParticipants,
                  })}
                </span>
              )}
            </div>
            <Button
              onClick={handleReserveClick}
              className="pdp__cta-btn primary-color"
            >
              {t("packageDeals.bookTour")}
            </Button>
          </div>
        </aside>
      </article>

      <PackageDealReserveModal
        open={reserveModalOpen}
        onClose={() => setReserveModalOpen(false)}
        deal={deal}
        selectedPrice={priceInfo?.amount}
        numPeople={useRoomPricing ? numPeople : 1}
        initialSelectedCombo={useRoomPricing ? selectedCombination : null}
      />
    </div>
  );
}

export default PackageDealDetailsPage;
